/**
 * Which tab a consult row belongs to — the ONE rule, kept pure.
 *
 * The practitioner's four questions are not four statuses: "upcoming" and "in progress"
 * are the SAME stored status (`scheduled`) split by the clock, because a scheduled
 * consult whose start time has passed is the one the practitioner is late for, not a
 * future appointment. Keeping the clock comparison here, injected as `nowIso`, is what
 * makes that half testable without mounting a list and stubbing `Date`.
 *
 * TOTAL BY CONSTRUCTION. Every row lands in exactly one bucket, including a
 * `sessionStatus` this build has never seen — an unrecognised state falls to `done`,
 * which is the bucket that offers NO actions. A row belonging to no bucket would simply
 * disappear from the screen, and there would be no surface left on which to notice it.
 *
 * `sessionStatus: null` IS NOT AN UNKNOWN (AMENDED 2026-09-03, finding F-06). Since the
 * practitioner's list is driven by bookings as well as sessions, `null` means exactly one
 * thing: NO ROOM HAS EVER BEEN OPENED for this appointment. That is a consultation still
 * ahead of the practitioner (or one they are late for) — a live card hold with nobody in
 * the room — so it is bucketed by the CLOCK exactly as `scheduled` is, not filed under
 * "done", which is where it used to land and where it offered no way to act on the hold.
 *
 * The vocabulary is `modules/gateway/schemas.py` `SESSION_STATES`; `i18n`'s
 * `consult.list.status.*` family is pinned against that same tuple by
 * `i18n/__tests__/keyCoverage.test.js`, so a new backend state cannot ship without a
 * label — but it can ship without a bucket, and this is where it lands when it does.
 */
// WHICH MODEL SOLD THE CONSULTATION IS THE SERVER'S ANSWER, read through the contract's
// one predicate. This module used to re-derive it as `authState == null &&
// !!bookingStatus` — a byte-identical copy of the one in `TelehealthAdmin.jsx` — which is
// reading an ABSENCE as a fact: a METERED row whose payment lookup failed, or whose
// booking has been purged, also has a null `authState`, and it then fell into the
// fixed-block branch below and was bucketed by a `scheduledEndAt` that model never sets.
import { isFixedBlock } from "../../gateway/contract";

/** Tab order, left to right — the order a practitioner reads their day in. */
export const CONSULT_BUCKETS = ["upcoming", "inProgress", "awaitingCompletion", "done"];

/** The minutes are being computed or are computed and not yet captured. */
const AWAITING_COMPLETION = ["computing", "computed"];

/** The room has never been opened (no session row) — see the header. */
export const isNotOpened = (row) => row?.sessionStatus == null;

/** A booking status that means the appointment is over, whatever the clock says. */
const TERMINAL_BOOKING_STATUSES = ["completed", "cancelled"];

/**
 * @param {{sessionStatus?: ?string, scheduledStartAt?: ?string}} row a `GET /tenant/consults` row
 * @param {string} [nowIso] the instant to compare against; defaults to the real clock
 * @returns {"upcoming"|"inProgress"|"awaitingCompletion"|"done"}
 */
export function bucketConsult(row, nowIso) {
  // A FIXED BLOCK IS BUCKETED BY ITS APPOINTMENT, NOT BY A SESSION IT WILL NEVER HAVE.
  // Falling through to the rule below would read its permanent `sessionStatus: null` as
  // "no room opened yet" and bucket it by the clock alone — so every consultation the
  // practice had ever delivered would sit under "in progress" for ever, because its start
  // is in the past and nothing about it would ever say it had finished.
  //
  // Its own end is knowable: `scheduledEndAt` is the booked block, published on the row.
  // Past it the consultation is over whether or not the completion sweeper has stamped it
  // yet, and there is nothing to act on either way.
  if (isFixedBlock(row)) {
    if (TERMINAL_BOOKING_STATUSES.includes(row.bookingStatus)) return "done";
    const from = Date.parse(row?.scheduledStartAt ?? "");
    const until = Date.parse(row?.scheduledEndAt ?? "");
    const at = nowIso == null ? Date.now() : Date.parse(nowIso);
    if (!Number.isFinite(from) || !Number.isFinite(at)) return "upcoming";
    if (at < from) return "upcoming";
    return Number.isFinite(until) && at >= until ? "done" : "inProgress";
  }

  const status = row?.sessionStatus;
  if (status === "in_progress") return "inProgress";
  if (AWAITING_COMPLETION.includes(status)) return "awaitingCompletion";
  // `== null` and not `!status`: an empty STRING is a value no writer produces and no
  // vocabulary contains, so it stays an unknown and keeps falling to "done".
  if (status !== "scheduled" && status != null) return "done";

  // A `scheduled` row with no usable start instant is NOT "in progress": we do not know
  // that it has begun, and claiming it has would put a live-call action on a row nothing
  // is happening on. Unknown start reads as "still ahead".
  const start = Date.parse(row?.scheduledStartAt ?? "");
  if (!Number.isFinite(start)) return "upcoming";
  const now = nowIso == null ? Date.now() : Date.parse(nowIso);
  if (!Number.isFinite(now)) return "upcoming";
  return start > now ? "upcoming" : "inProgress";
}

export default bucketConsult;
